import React, { useState, useRef, useEffect } from "react";
import "./CustomCountryCodeSelect.css";

/* ── Flag image via flagcdn.com (works on all platforms incl. Windows) ── */
const FlagImg = ({ iso, size = 20, className = "" }) => {
  if (!iso) return null;
  const code = iso.toLowerCase();
  return (
    <img
      src={`https://flagcdn.com/w${size}/${code}.png`}
      srcSet={`https://flagcdn.com/w${size * 2}/${code}.png 2x`}
      width={size}
      height={Math.round(size * 0.75)}
      alt={iso}
      className={className}
      onError={(e) => {
        e.currentTarget.style.display = "none";
      }}
      draggable={false}
    />
  );
};

const CustomCountryCodeSelect = ({ value, onChange, options }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggle = () => {
    setIsOpen((o) => !o);
    if (!isOpen) setSearchTerm("");
  };

  const handleSelect = (option) => {
    if (onChange) onChange(option);
    setIsOpen(false);
  };

  const filteredOptions = (options || []).filter((opt) => {
    const q = searchTerm?.toLowerCase();
    return (
      opt?.label?.toLowerCase().includes(q) ||
      opt?.value?.toLowerCase().includes(q) ||
      (opt?.iso && opt.iso.toLowerCase().includes(q))
    );
  });

  return (
    <div className="custom-country-select-container" ref={dropdownRef}>
      <button
        type="button"
        className={`custom-country-select-trigger${isOpen ? " active" : ""}`}
        onClick={handleToggle}
        aria-label="Select country code"
      >
        {/* Flag image */}
        {value?.iso ? (
          <FlagImg iso={value.iso} size={20} className="ccs-flag-img" />
        ) : value?.flag ? (
          <span className="ccs-flag-emoji" aria-hidden="true">
            {value?.flag}
          </span>
        ) : null}
        <span className="ccs-code">{value?.value || "+91"}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`ccs-chevron${isOpen ? " ccs-chevron--open" : ""}`}
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {isOpen && (
        <div className="custom-country-select-dropdown">
          <div className="custom-country-search-wrapper">
            <svg
              className="ccs-search-icon"
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              type="text"
              placeholder="Search country or code…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="custom-country-search-input"
              autoFocus
            />
          </div>
          <ul className="custom-country-options-list" role="listbox">
            {filteredOptions?.length > 0 ? (
              filteredOptions?.map((option) => {
                const isSelected = value?.value === option?.value && value?.iso === option?.iso;
                return (
                  <li
                    key={(option?.value || "") + (option?.iso || "")}
                    role="option"
                    aria-selected={isSelected}
                    className={`custom-country-option-item${isSelected ? " country-selected" : ""}`}
                    onClick={() => handleSelect(option)}
                  >
                    {/* Flag */}
                    {option?.iso ? (
                      <FlagImg iso={option.iso} size={20} className="ccs-opt-flag-img" />
                    ) : option?.flag ? (
                      <span className="ccs-opt-flag-emoji" aria-hidden="true">
                        {option?.flag}
                      </span>
                    ) : (
                      <span className="ccs-opt-flag-placeholder" />
                    )}
                    {/* Dial code pill */}
                    <span className="ccs-opt-code">{option?.value}</span>
                    {/* Country name */}
                    <span className="ccs-opt-name">{option?.label?.replace(/\s*\(.*\)/, "")}</span>
                    {/* Selected checkmark */}
                    {isSelected && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#2a71ff"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="ccs-check-icon"
                        aria-hidden="true"
                      >
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    )}
                  </li>
                );
              })
            ) : (
              <li className="custom-country-no-options">No matches found</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CustomCountryCodeSelect;
